import { useMemo, useState } from "react";
import { Map, Marker, Polygon, Polyline } from "@vis.gl/react-google-maps";
import {
  boundsOf,
  computeHeadingDeg,
  MOWER_WIDTH_METERS,
  segmentPath,
  type GpsPoint,
  type PerimeterPoint,
} from "../lib/sessionDetail";
import {
  GOOGLE_MAPS_MAP_ID,
  hasGoogleMapsKey,
  metersPerPixel,
} from "../lib/googleMaps";
import { MapKeyMissing } from "./GoogleMapsProvider";
import {
  FitBounds,
  GroundOverlay,
  useMapCenterLat,
  useMapZoom,
  type LatLngBoundsTuple,
} from "./GoogleMapOverlays";

const MOWING_COLOR = "#3b82f6"; // blue — blades on
const TRANSIT_COLOR = "#9ca3af"; // grey — driving, blades off
const PERIMETER_COLOR = "#f59e0b"; // amber — field boundary

type ViewMode = "path" | "coverage";
type BaseLayer = "sat" | "street";

/** A dash pattern for a Maps polyline: Maps has no `dashArray`, so a dash is a
 *  repeated line symbol drawn along an otherwise invisible stroke. */
function dashedStroke(repeatPx: number): google.maps.PolylineOptions["icons"] {
  return [
    {
      icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 2 },
      offset: "0",
      repeat: repeatPx + "px",
    },
  ];
}

const toLatLng = ([lat, lng]: [number, number]) => ({ lat, lng });

/**
 * Wide translucent strokes under the mowing segments approximating the real
 * cutting swath. Maps stroke weight is in pixels, so we convert the deck width
 * (metres) to pixels for the current latitude + zoom and redraw on zoom.
 */
function MowingSwaths({ segments }: { segments: ReturnType<typeof segmentPath> }) {
  const zoom = useMapZoom();
  const lat = useMapCenterLat();
  const weight = Math.max(2, MOWER_WIDTH_METERS / metersPerPixel(lat, zoom));
  return (
    <>
      {segments
        .filter((s) => s.mowing)
        .map((s, i) => (
          <Polyline
            key={"swath-" + i}
            path={s.positions.map(toLatLng)}
            strokeColor={MOWING_COLOR}
            strokeWeight={weight}
            strokeOpacity={0.25}
            clickable={false}
            zIndex={1}
          />
        ))}
    </>
  );
}

/** A north-up arrow rotated to the robot's heading. Drawn as a marker symbol so
 *  it needs no Cloud map ID and rotates without a DOM overlay. */
function headingIcon(heading: number): google.maps.Symbol {
  return {
    path: "M 0,-11 L 7,9 L 0,5 L -7,9 Z",
    scale: 1,
    rotation: heading,
    fillColor: MOWING_COLOR,
    fillOpacity: 1,
    strokeColor: "#fff",
    strokeWeight: 1.5,
  };
}

const START_ICON: google.maps.Symbol = {
  path: "M -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0",
  scale: 1,
  fillColor: "#22c55e",
  fillOpacity: 1,
  strokeColor: "#fff",
  strokeWeight: 2,
};

export function SessionPathMap({
  fullPath,
  visiblePath,
  perimeter,
  coverageBase64,
  height = 340,
}: {
  fullPath: GpsPoint[];
  visiblePath: GpsPoint[];
  perimeter: PerimeterPoint[];
  coverageBase64: string | null;
  height?: number;
}) {
  const [view, setView] = useState<ViewMode>("path");
  const [base, setBase] = useState<BaseLayer>("sat");

  const bounds = useMemo(
    () => boundsOf(fullPath, perimeter) as LatLngBoundsTuple | null,
    [fullPath, perimeter],
  );
  const segments = useMemo(() => segmentPath(visiblePath), [visiblePath]);
  const heading = useMemo(() => computeHeadingDeg(visiblePath), [visiblePath]);

  const perimeterLatLngs = useMemo(
    () => perimeter.map((p) => ({ lat: p.latitude, lng: p.longitude })),
    [perimeter],
  );
  const robot = visiblePath.length > 0 ? visiblePath[visiblePath.length - 1] : null;
  const start = fullPath.length > 0 ? fullPath[0] : null;

  if (!hasGoogleMapsKey) return <MapKeyMissing height={height} />;

  if (!bounds) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-background-secondary)",
          color: "var(--color-text-tertiary)",
          fontSize: 12,
          borderRadius: "var(--border-radius-md)",
        }}
      >
        No GPS path recorded for this session.
      </div>
    );
  }

  const coverageUrl = coverageBase64
    ? "data:image/png;base64," + coverageBase64
    : null;

  return (
    <div
      className="map-shell"
      style={{ borderRadius: "var(--border-radius-md)", overflow: "hidden" }}
    >
      <Map
        mapId={GOOGLE_MAPS_MAP_ID}
        // Placeholders — FitBounds frames the session on first load, then
        // leaves the view alone so scrubbing doesn't yank it back.
        defaultCenter={{ lat: 0, lng: 0 }}
        defaultZoom={16}
        mapTypeId={base === "sat" ? "satellite" : "roadmap"}
        style={{ height, width: "100%", background: "#0d1117" }}
        gestureHandling="greedy"
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        clickableIcons={false}
        // Imagery runs out before the zoom does; letting the user keep zooming
        // on stretched tiles beats stopping short of the pass spacing they're
        // trying to see.
        maxZoom={22}
      >
        <FitBounds bounds={bounds} padding={30} maxZoom={20} once />

        {perimeterLatLngs.length > 2 && (
          <>
            <Polygon
              paths={perimeterLatLngs}
              strokeOpacity={0}
              fillColor={PERIMETER_COLOR}
              fillOpacity={view === "coverage" ? 0.05 : 0.12}
              clickable={false}
              zIndex={0}
            />
            {/* The dashed outline is its own polyline: Maps supports repeated
                line symbols on polylines only, not on a polygon's stroke. */}
            <Polyline
              path={[...perimeterLatLngs, perimeterLatLngs[0]]}
              strokeColor={PERIMETER_COLOR}
              strokeOpacity={0}
              strokeWeight={2}
              icons={dashedStroke(12)}
              clickable={false}
              zIndex={2}
            />
          </>
        )}

        {view === "coverage" && coverageUrl && (
          <GroundOverlay url={coverageUrl} bounds={bounds} opacity={0.7} />
        )}

        {view === "path" && (
          <>
            <MowingSwaths segments={segments} />
            {segments.map((s, i) => (
              <Polyline
                key={"line-" + i}
                path={s.positions.map(toLatLng)}
                strokeColor={s.mowing ? MOWING_COLOR : TRANSIT_COLOR}
                strokeWeight={s.mowing ? 2.5 : 1.75}
                // Transit is dashed, and a dash pattern is drawn by symbols on
                // an invisible stroke — hence opacity 0 for that case only.
                strokeOpacity={s.mowing ? 1 : 0}
                icons={s.mowing ? undefined : dashedStroke(8)}
                clickable={false}
                zIndex={3}
              />
            ))}
            {start && (
              <Marker
                position={{ lat: start.latitude, lng: start.longitude }}
                icon={START_ICON}
                clickable={false}
                title="Session start"
                zIndex={4}
              />
            )}
            {robot && (
              <Marker
                position={{ lat: robot.latitude, lng: robot.longitude }}
                icon={headingIcon(heading)}
                clickable={false}
                title="Current position"
                zIndex={5}
              />
            )}
          </>
        )}
      </Map>

      {/* Overlaid controls */}
      <div style={{ position: "absolute", top: 8, right: 8, zIndex: 500, display: "flex", gap: 6 }}>
        <div className="map-toggle">
          <button className={view === "path" ? "on" : ""} onClick={() => setView("path")}>
            Path
          </button>
          <button
            className={view === "coverage" ? "on" : ""}
            onClick={() => setView("coverage")}
            disabled={!coverageUrl}
            title={coverageUrl ? "" : "Coverage not available"}
          >
            Coverage
          </button>
        </div>
        <div className="map-toggle">
          <button className={base === "sat" ? "on" : ""} onClick={() => setBase("sat")}>
            Sat
          </button>
          <button className={base === "street" ? "on" : ""} onClick={() => setBase("street")}>
            Map
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="map-legend">
        {view === "path" ? (
          <>
            <span><i style={{ background: MOWING_COLOR }} /> Mowing</span>
            <span><i style={{ background: TRANSIT_COLOR }} /> Transit</span>
            <span><i style={{ background: PERIMETER_COLOR }} /> Boundary</span>
          </>
        ) : (
          <>
            <span><i style={{ background: "#f8b6b6" }} /> Unmowed</span>
            <span><i style={{ background: "#aad7ff" }} /> 1 pass</span>
            <span><i style={{ background: "#0f3ca0" }} /> Overlap</span>
          </>
        )}
      </div>
    </div>
  );
}
